import React from 'react';
import RadioButton from './radio-button';

interface Option {
  value: number | string;
  label: string;
}

interface GroupRadioButtonProps {
  /** Id for the input field to link to the label */
  id: string;
  /** This will be the selected value */
  selectedButton: Option['value'];
  /** Adds required attribute */
  required?: boolean;
  /** Disables radio button disallowing interaction */
  disabled?: boolean;
  /** The html name attribute for the button group. This should be unique for each group */
  name: string;
  /** Adds additional classes to the component. */
  className?: string;
  /** Callback when radio button is clicked */
  onClick: (value: Option['value']) => void;
  /** Options will hold objects with the value as a number or string, and the labels as string to count the number of radio buttons add to this group. Eg: mockOptions = [{ value: '0', label: 'Option 1' }, { value: '1', label: 'Option 2' }] */
  options: Option[];
  /** Will define if the radio buttons have borders */
  border?: boolean;
}

/**
 * Grouped Radio Buttons
 *
 * @param {GroupRadioButtonProps} props
 *
 * @returns JSX.ELEMENT
 */
export const GroupRadioButton = ({
  id,
  name,
  selectedButton,
  className,
  onClick,
  options,
  border,
  required,
  disabled = false
}: GroupRadioButtonProps) => {
  return (
    <div id={id} className={className}>
      {options.map((option: Option) => (
        <RadioButton
          id={`${id}-radiobutton-${option.value}`}
          border={border}
          data-testid={`group-radio-button`}
          checked={selectedButton === option.value}
          required={required}
          disabled={disabled}
          key={option.value}
          value={option.value}
          name={name}
          {...(className ? { className: `${className}-input` } : {})}
          onClick={e => {
            onClick(e.currentTarget.value);
          }}
        >
          {option.label}
        </RadioButton>
      ))}
    </div>
  );
};

export default GroupRadioButton;
